import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { yourPlace, type ArticleView, type EditionView, type SeoResponse, type ArticleSourceView } from "@joogoos/contracts";
import { api } from "../api.js";
import { Icon } from "../ui/icons.js";
import { useNarrow } from "../ui/narrow.js";
import { Card, HEADLINE, KICKER, SideHead, SideMenu, sideIcon, Tab, UNDER } from "./chrome.js";
import { ConnectSite, SiteSettings } from "./SeoSite.js";
import { GoogleSays } from "./SeoGoogle.js";

/**
 * SEO — the last tab (ADR 0033, [21 §9](../../../../docs/21-articles-and-seo.md#9--where-it-appears-in-the-app)).
 *
 * Articles written from the business's own sources, approved by a person, and published on the customer's own
 * domain, so the search credit is theirs.
 *
 * The shape is the Studio's: a worklist on the left and the thing itself on the right. The worklist is grouped by
 * what it wants from you rather than by status, because the only question somebody opening this tab has is *what
 * do I have to do* — and the answer is usually "read these two drafts", not "here are eleven rows".
 *
 * What the screen must never do is imply the checking is finished. Flags are pinned above the words, not tucked in
 * a panel, Approve carries its own count, and a sentence somebody stood behind says so where it stands. The code
 * catches what code can catch; the person catches the rest, and the screen is honest about which is which.
 */
type Half = "articles" | "google" | "site";
const HALVES: ReadonlyArray<{ key: Half; label: string; hint: string; icon: string }> = [
  { key: "articles", label: "Articles", hint: "drafts to read, and what is out", icon: "type" },
  { key: "google", label: "What Google says", hint: "searches you already show up in", icon: "search" },
  { key: "site", label: "Your website", hint: "where the articles go", icon: "globe" },
];

export function Seo({ onBusiness, onConnection }: {
  onBusiness?: (() => void) | undefined;
  /** Business → SEO, where Search Console is connected and disconnected. */
  onConnection?: (() => void) | undefined;
} = {}) {
  const qc = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const [lang, setLang] = useState<string | null>(null);
  /**
   * Three halves, and none of them is a settings page behind the others. *Did any of this work* is a question
   * somebody opens the tab to ask, and so is *where do these actually go* — so both are one press away, and
   * pressing one never takes the tab's own heading and its other two presses off the screen.
   */
  const [half, setHalf] = useState<Half>(() => (new URLSearchParams(location.search).has("google") ? "google" : "articles"));
  const narrow = useNarrow();
  /**
   * Back from Google's consent (`?google=connected`): What Google says, where the property is picked next. It opened
   * on Articles, where somebody who had just connected read "No topics yet" and no next step (live, 2026-09-29).
   * The word is taken out of the address once read, as the mailbox's is, so a reload lands where it normally would.
   */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has("google")) return;
    params.delete("google");
    const rest = params.toString();
    history.replaceState(history.state, "", `${location.pathname}${rest ? `?${rest}` : ""}${location.hash}`);
  }, []);

  const q = useQuery({
    queryKey: ["seo"],
    queryFn: () => api.seo(),
    // Only while something is being written: a tab sitting still asks for nothing (docs/17 §performance).
    refetchInterval: (query) => ((query.state.data as SeoResponse | undefined)?.working.length ? 4_000 : false),
  });
  const seo = q.data;
  const refresh = () => qc.invalidateQueries({ queryKey: ["seo"] });

  const open = seo?.articles.find((a) => a.id === openId) ?? null;
  useEffect(() => { if (openId && seo && !open) setOpenId(null); }, [openId, seo, open]);

  if (q.isPending) return <div className="h-full grid place-items-center text-faint text-sm">…</div>;
  if (!seo) return <div className="h-full grid place-items-center text-faint text-sm">SEO could not be read.</div>;
  if (!seo.site) return <ConnectSite address={seo.address} suggested={seo.suggested} onDone={refresh} onBusiness={onBusiness} />;

  const list = (
    <Worklist
      seo={seo}
      openId={openId}
      onOpen={(id) => { setOpenId(id); setLang(null); }}
      onChanged={refresh}
    />
  );

  return (
    <div className={narrow ? "h-full flex flex-col min-h-0" : "h-full flex min-h-0"}>
      <SideMenu label="SEO" current={half} onPick={setHalf} narrow={narrow}
        head={<SideHead icon="seo" title="SEO" note={seo.site.baseUrl.replace(/^https?:\/\//, "")} />}
        items={HALVES.map((h) => ({ ...h, mark: sideIcon(h.icon), ...(h.key === "google" ? { count: seo.google?.opportunities.length ?? 0 } : {}) }))}
        // Always, on all three halves, and apart from them: it used to appear only on Articles, so the row
        // reshuffled as you moved and a coral press sat flush against the views, reading as a fourth one.
        action={<PlanButton seo={seo} onDone={() => { refresh(); setHalf("articles"); }} />}
      />
      <div className="flex-1 min-w-0 min-h-0 flex flex-col">
        {half === "google" ? (
          <div className="flex-1 min-h-0 overflow-y-auto px-4 md:px-6 py-4">
            <div className="max-w-3xl"><GoogleSays seo={seo} onChanged={refresh} onConnection={onConnection} /></div>
          </div>
        ) : half === "site" ? (
          <div className="flex-1 min-h-0 overflow-y-auto px-4 md:px-6 py-4">
            <SiteSettings seo={seo} onDone={refresh} onBusiness={onBusiness} />
          </div>
        ) : narrow ? (
          <div className="flex-1 min-h-0 overflow-y-auto">
            {open
              ? <Article seo={seo} article={open} lang={lang} onLang={setLang} onBack={() => setOpenId(null)} onChanged={refresh} narrow />
              : list}
          </div>
        ) : (
          <div className="flex-1 min-h-0 grid grid-cols-[320px_1fr]">
            <div className="border-r border-line bg-rail overflow-y-auto">{list}</div>
            <div className="overflow-y-auto">
              {open
                ? <Article seo={seo} article={open} lang={lang} onLang={setLang} onBack={() => setOpenId(null)} onChanged={refresh} narrow={false} />
                : <Nothing seo={seo} />}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** The one button that costs money before anything exists, with its price on it (13). */
function PlanButton({ seo, onDone }: { seo: SeoResponse; onDone: () => void }) {
  const busy = seo.working.includes("plan_topics");
  const m = useMutation({ mutationFn: () => api.planTopics(), onSuccess: onDone });
  return (
    <button
      onClick={() => m.mutate()}
      disabled={busy || m.isPending}
      className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-coral text-white disabled:opacity-55"
    >
      {busy ? "Thinking of topics…" : `Plan topics · $${seo.prices.planTopics}`}
    </button>
  );
}

/**
 * The list, grouped by what it wants from you.
 *
 * *Waiting for you* first and always, even when it is empty, because its emptiness is the answer to the question
 * the tab was opened with.
 */
function Worklist({ seo, openId, onOpen, onChanged }: { seo: SeoResponse; openId: string | null; onOpen: (id: string) => void; onChanged: () => void }) {
  const groups = useMemo(() => groupArticles(seo.articles), [seo.articles]);
  return (
    <div className="py-2">
      {groups.map(([title, items]) => {
        // An empty group is not an answer to anything, with one exception: *Waiting for you* empty is the answer
        // to the question the tab was opened with, so it is the only one that draws itself with nothing in it.
        if (!items.length && title !== "Waiting for you") return null;
        return (
          <section key={title} className="mb-1.5">
            <h2 className="px-4 pt-3 pb-1 flex items-center gap-1.5">
              <span className="text-[10.5px] font-bold uppercase tracking-[.07em] text-muted">{title}</span>
              {items.length ? (
                <span className="px-1.5 rounded-full bg-line-2/50 text-[10px] font-bold tabular-nums text-ink-2">{items.length}</span>
              ) : null}
            </h2>
            {items.length === 0 ? (
              <p className="px-4 pb-1 text-[12.5px] text-faint leading-relaxed">Nothing needs you. Anything being written turns up here when it is ready to read.</p>
            ) : items.map((a) => (
              <Row key={a.id} article={a} on={a.id === openId} onOpen={() => onOpen(a.id)} onChanged={onChanged} seo={seo} />
            ))}
          </section>
        );
      })}
    </div>
  );
}

export function groupArticles(articles: ArticleView[]): Array<[string, ArticleView[]]> {
  const waiting: ArticleView[] = [], proposed: ArticleView[] = [], writing: ArticleView[] = [], published: ArticleView[] = [];
  for (const a of articles) {
    if (a.status === "dropped") continue;
    if (a.status === "proposed") { proposed.push(a); continue; }
    if (a.editions.some((e) => e.status === "drafting")) { writing.push(a); continue; }
    if (a.editions.some((e) => e.status === "published" || e.status === "updated")) { published.push(a); continue; }
    waiting.push(a);
  }
  return [["Waiting for you", waiting], ["Proposed topics", proposed], ["Being written", writing], ["Published", published]];
}

function Row({ article, on, onOpen, onChanged, seo }: { article: ArticleView; on: boolean; onOpen: () => void; onChanged: () => void; seo: SeoResponse }) {
  const accept = useMutation({ mutationFn: () => api.acceptTopic(article.id), onSuccess: onChanged });
  const drop = useMutation({ mutationFn: () => api.dropTopic(article.id), onSuccess: onChanged });
  const flags = article.editions.reduce((n, e) => n + e.openFlags, 0);
  const title = article.editions[0]?.title || article.targetPhrase;
  return (
    <div className={`mx-2 px-2.5 py-2 rounded-lg border-l-2 ${on ? "border-coral bg-panel shadow-[0_1px_2px_rgba(23,24,23,.06)]" : "border-transparent hover:bg-panel/70"}`}>
      <button onClick={onOpen} className="w-full text-left">
        <span className="block text-[13.5px] font-medium leading-snug">{title}</span>
        {title === article.targetPhrase ? null : <span className="block text-[11.5px] text-muted mt-0.5">{article.targetPhrase}</span>}
      </button>
      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
        {article.editions.map((e) => <Pill key={e.id} edition={e} />)}
        {flags ? <span className="px-1.5 py-0.5 rounded bg-coral/12 text-[10px] font-bold uppercase tracking-wide text-coral tabular-nums">{flags} to resolve</span> : null}
        {article.status === "proposed" ? (
          <>
            <button onClick={() => accept.mutate()} disabled={accept.isPending} className="px-2 py-0.5 rounded-md bg-ink text-white text-[11px] font-semibold disabled:opacity-55">Accept</button>
            <button onClick={() => drop.mutate()} disabled={drop.isPending} className="px-2 py-0.5 rounded-md text-[11px] font-medium text-muted hover:text-ink">Drop</button>
          </>
        ) : null}
        {article.status === "accepted" && !article.editions.length ? <WriteButton article={article} seo={seo} onDone={onChanged} small /> : null}
      </div>
    </div>
  );
}

const PILL: Record<EditionView["status"], string> = {
  drafting: "bg-line-2/40 text-muted", draft: "bg-line-2/40 text-ink-2", approved: "bg-ink text-white",
  published: "bg-good/15 text-good", updated: "bg-good/15 text-good", withdrawn: "bg-line-2/40 text-faint", failed: "bg-coral/15 text-coral",
};

const SAYS: Record<EditionView["status"], string> = {
  drafting: "writing", draft: "draft", approved: "approved", published: "live", updated: "live", withdrawn: "withdrawn", failed: "failed",
};

function Pill({ edition }: { edition: EditionView }) {
  const live = edition.status === "published" || edition.status === "updated";
  const word = live && !edition.seenLiveAt ? "sent" : SAYS[edition.status];
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${PILL[edition.status]}`}>
      {edition.lang} · {word}
    </span>
  );
}

function WriteButton({ article, seo, onDone, small }: { article: ArticleView; seo: SeoResponse; onDone: () => void; small?: boolean }) {
  const busy = seo.working.includes(article.id);
  const m = useMutation({ mutationFn: () => api.writeArticle(article.id), onSuccess: onDone });
  return (
    <button
      onClick={() => m.mutate()}
      disabled={busy || m.isPending}
      className={small
        ? "px-2 py-0.5 rounded-md bg-ink text-white text-[11px] font-semibold disabled:opacity-55"
        : "px-3 py-1.5 rounded-lg text-xs font-semibold bg-coral text-white disabled:opacity-55"}
    >
      {busy ? "Writing…" : `Write · $${seo.prices.writeArticle}`}
    </button>
  );
}

function Nothing({ seo }: { seo: SeoResponse }) {
  const waiting = seo.articles.filter((a) => a.status !== "dropped").length;
  return (
    <div className="h-full grid place-items-center px-8 py-10">
      <div className="max-w-[46ch] text-center">
        <p className={`${KICKER} text-muted`}>{waiting ? "Your articles" : "Nothing written yet"}</p>
        <h2 className={HEADLINE}>{waiting ? "Pick something on the left." : "No topics yet."}</h2>
        <p className={`${UNDER} mx-auto`}>
          {waiting
            ? "Every article is written only from your own sources, and nothing is published until you approve it."
            : "Plan topics reads your business page and your sources, and proposes what people are searching for that you can answer better than anybody."}
        </p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------------------------ the article ---- */

type Pane = "article" | "flags" | "sources";

function Article({ seo, article, lang, onLang, onBack, onChanged, narrow }:
  { seo: SeoResponse; article: ArticleView; lang: string | null; onLang: (l: string) => void; onBack: () => void; onChanged: () => void; narrow: boolean }) {
  const edition = article.editions.find((e) => e.lang === lang) ?? article.editions[0] ?? null;
  const [pane, setPane] = useState<Pane>("article");
  const detail = useQuery({ queryKey: ["seo", "article", article.id], queryFn: () => api.article(article.id), enabled: Boolean(edition) });

  if (!edition) return <Unwritten seo={seo} article={article} onChanged={onChanged} onBack={onBack} narrow={narrow} />;

  const open = edition.flags.filter((f) => f.resolution === "open");
  const sources = detail.data?.sources ?? [];

  return (
    <div className="px-5 md:px-7 py-5">
      {narrow ? (
        <button onClick={onBack} className="mb-3 flex items-center gap-1 text-[12.5px] font-medium text-muted hover:text-ink">
          <Icon name="out" size={14} className="rotate-180" /> All articles
        </button>
      ) : null}

      <div className="flex flex-wrap items-center gap-1.5 mb-3">
        {article.editions.map((e) => (
          <Tab key={e.id} on={e.id === edition.id} onClick={() => onLang(e.lang)}>
            {e.lang.toUpperCase()}{e.openFlags ? ` · ${e.openFlags}` : ""}
          </Tab>
        ))}
        <span className="flex-1" />
        <Actions seo={seo} article={article} edition={edition} onChanged={onChanged} />
      </div>

      {edition.sample ? <Note tone="muted">Sample text — not written by the AI.</Note> : null}
      {edition.behind ? <Note tone="muted">Behind the {article.editions[0]?.lang.toUpperCase()}. Translate again to catch it up; this one stays live until you do.</Note> : null}
      {edition.error ? <Note tone="coral">{edition.error}</Note> : null}
      {edition.status === "drafting" ? <Note tone="muted">Being written…</Note> : null}
      {edition.status === "published" || edition.status === "updated" ? (
        <Note tone={edition.seenLiveAt ? "good" : "muted"}>
          {edition.seenLiveAt
            ? <>Live on {seo.site!.baseUrl.replace(/^https?:\/\//, "")} — <a href={edition.url} target="_blank" rel="noreferrer" className="underline">{edition.path}</a></>
            : <>Sent — not on the site yet. {seo.site!.buildHookSet ? "Its build was asked for; this says Live once the page answers." : "No build hook is set, so the site has not been asked to rebuild."}</>}
        </Note>
      ) : null}

      {narrow ? (
        <div className="flex gap-1.5 mb-3">
          {(["article", "flags", "sources"] as Pane[]).map((p) => (
            <Tab key={p} on={pane === p} onClick={() => setPane(p)}>
              {p === "flags" ? `Flags${open.length ? ` (${open.length})` : ""}` : p === "sources" ? "Sources" : "Article"}
            </Tab>
          ))}
        </div>
      ) : null}

      {(!narrow || pane === "flags") && open.length ? <Flags edition={edition} sources={sources} onChanged={onChanged} /> : null}
      {!narrow || pane === "article" ? <Body edition={edition} onChanged={onChanged} /> : null}
      {(!narrow || pane === "sources") ? <Sources edition={edition} sources={sources} /> : null}
      {/* The research stays with the article after it is written: it is how somebody sees whether they are
          actually competing, and how the questions get answered again when Google's change. */}
      {(!narrow || pane === "sources") ? <div className="max-w-3xl"><Serp article={article} seo={seo} onChanged={onChanged} /></div> : null}
    </div>
  );
}

function Unwritten({ seo, article, onChanged, onBack, narrow }:
  { seo: SeoResponse; article: ArticleView; onChanged: () => void; onBack: () => void; narrow: boolean }) {
  return (
    <div className="px-5 md:px-7 py-5 max-w-2xl">
      {narrow ? (
        <button onClick={onBack} className="mb-3 flex items-center gap-1 text-[12.5px] font-medium text-muted hover:text-ink">
          <Icon name="out" size={14} className="rotate-180" /> All articles
        </button>
      ) : null}
      <p className={`${KICKER} text-muted`}>{article.status === "proposed" ? "A topic to decide on" : "Accepted, not written yet"}</p>
      <h1 className={HEADLINE}>{article.targetPhrase}</h1>
      <div className="mt-4">
        <Card>
          <dl className="grid grid-cols-[92px_1fr] gap-y-2 text-[13px]">
            <dt className="text-muted">For</dt><dd>{article.audience || "—"}</dd>
            <dt className="text-muted">Intent</dt><dd>{article.intent}</dd>
            <dt className="text-muted">Angle</dt><dd className="leading-relaxed">{article.angle || "—"}</dd>
            <dt className="text-muted">Sources</dt><dd className="tabular-nums">{article.sourceRefs.join(", ") || "all of them"}</dd>
          </dl>
        </Card>
      </div>
      {/* The research, then the press that spends: one search says who already ranks and what people ask next, and
          it costs a tenth of what writing does. Putting Write above it would be asking for the money first. */}
      <Serp article={article} seo={seo} onChanged={onChanged} />
      <div className="mt-4">
        {article.status === "accepted"
          ? <WriteButton article={article} seo={seo} onDone={onChanged} />
          : <p className="text-[12.5px] text-muted leading-relaxed">Accept this topic on the left to write it. Accepting is free; writing is what costs.</p>}
      </div>
    </div>
  );
}

/**
 * What Google already answers for this phrase, before a penny is spent writing for it (21 §3).
 *
 * The honest thing on this panel is what is missing from it. There is no search volume here, because no search API
 * gives that away for a tenth of a cent and a made-up number is worse than none. What it can show is what is
 * really there: who ranks now, and what people ask next. The first tells you what you are up against; the second
 * is the FAQ somebody else already researched, and it is the half that goes to the writer.
 */
function Serp({ article, seo, onChanged }: { article: ArticleView; seo: SeoResponse; onChanged: () => void }) {
  const m = useMutation({ mutationFn: () => api.readSerp(article.id), onSuccess: onChanged });
  const busy = seo.working.includes(article.id);
  const serp = article.serp;

  if (!serp) {
    return (
      <div className="mt-4 rounded-xl border border-dashed border-line-2 bg-panel px-4 py-3.5">
        <p className="text-[13px] font-semibold">Nobody has looked at the search results for this yet.</p>
        <p className="text-[12.5px] text-muted mt-0.5 leading-relaxed max-w-[60ch]">
          One search tells you who already ranks for it and what people ask next. Worth doing before you pay to
          write: it is the difference between writing for this phrase and guessing at it.
        </p>
        <button onClick={() => m.mutate()} disabled={busy || m.isPending}
          className="mt-2.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-ink text-white disabled:opacity-55">
          {busy || m.isPending ? "Searching…" : `Check the search results · $${seo.prices.readSerp}`}
        </button>
      </div>
    );
  }

  // Where this business comes, found by its address in the ten already read: free, and needs no Google account.
  const place = yourPlace(serp, seo.address ?? seo.site?.baseUrl);
  return (
    <div className="mt-4">
      <Card
        title={`What Google answers in ${serp.country.toUpperCase()}`}
        note={`read ${new Date(serp.readAt).toLocaleDateString()}`}
        action={
          <button onClick={() => m.mutate()} disabled={busy || m.isPending}
            className="text-[11.5px] font-medium text-muted hover:text-ink disabled:opacity-55">
            Look again · ${seo.prices.readSerp}
          </button>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-[13px] leading-relaxed">
            {place
              ? <><b className="text-good">Your site comes #{place}</b> for this phrase.</>
              : <><b>Your site is not in the first ten</b> for this phrase.</>}
          </p>

          {serp.questions.length ? (
            <div>
              <h3 className={`${KICKER} text-muted`}>What people ask next</h3>
              <ul className="mt-1.5 flex flex-col gap-1">
                {serp.questions.map((q) => (
                  <li key={q} className="text-[12.5px] text-ink-2 leading-relaxed pl-3 border-l-2 border-line">{q}</li>
                ))}
              </ul>
              <p className="mt-1.5 text-[11.5px] text-faint leading-relaxed">These go to the writer, and usually become the FAQ.</p>
            </div>
          ) : null}

          {serp.related.length ? (
            <div>
              <h3 className={`${KICKER} text-muted`}>Other ways people type it</h3>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {serp.related.map((r) => (
                  <span key={r} className="px-2 py-0.5 rounded-full bg-page border border-line text-[11.5px] text-ink-2">{r}</span>
                ))}
              </div>
            </div>
          ) : null}

          {serp.results.length ? (
            <div>
              <h3 className={`${KICKER} text-muted`}>Who ranks for it now</h3>
              <ol className="mt-1.5 flex flex-col gap-1.5">
                {serp.results.map((r) => (
                  <li key={r.link} className="flex gap-2.5 text-[12.5px] leading-snug">
                    <span className="tabular-nums text-[11px] font-bold text-faint w-4 shrink-0 pt-0.5">{r.position}</span>
                    <span className="min-w-0">
                      <a href={webHref(r.link)} target="_blank" rel="noreferrer" className="font-medium text-ink hover:text-coral">{r.title}</a>
                      <span className="block text-[11.5px] text-muted">
                        {r.domain}{r.position === place ? <b className="ml-1.5 text-good">· you</b> : null}
                      </span>
                    </span>
                  </li>
                ))}
              </ol>
              {/* Said out loud, because it is a rule and not an omission (ADR 0033 §1). */}
              <p className="mt-2 text-[11.5px] text-faint leading-relaxed">
                This list is for you, and is never given to the writer: these are the competitors, and a page of
                search results is exactly where their names would get into your article.
              </p>
            </div>
          ) : null}

          <p className="text-[11.5px] text-faint leading-relaxed border-t border-line pt-2">
            No search volume: nobody sells that for a tenth of a cent, and a made-up number would be worse than none.
          </p>
        </div>
      </Card>
    </div>
  );
}

/** Approve, Publish, Withdraw and Translate. Free, and each asks once, because each is somebody else's website. */
function Actions({ seo, article, edition, onChanged }: { seo: SeoResponse; article: ArticleView; edition: EditionView; onChanged: () => void }) {
  const approve = useMutation({ mutationFn: () => api.approveEdition(edition.id), onSuccess: onChanged });
  const publish = useMutation({ mutationFn: () => api.publishArticle(article.id), onSuccess: onChanged });
  const withdraw = useMutation({ mutationFn: () => api.withdrawArticle(article.id), onSuccess: onChanged });
  const translate = useMutation({ mutationFn: () => api.translateArticle(article.id), onSuccess: onChanged });
  const write = useMutation({ mutationFn: () => api.writeArticle(article.id), onSuccess: onChanged });
  const ask = (m: { mutate: () => void }, question: string) => () => { if (confirm(question)) m.mutate(); };

  const live = edition.status === "published" || edition.status === "updated";
  const others = (seo.site?.languages.length ?? 1) > 1;
  const anyApproved = article.editions.some((e) => e.approvedAt);
  const error = (approve.error ?? publish.error ?? withdraw.error ?? translate.error) as Error | null;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {error ? <span className="text-[11.5px] text-coral">{error.message}</span> : null}
      {edition.status === "draft" || edition.status === "failed" ? (
        <button
          onClick={() => approve.mutate()}
          disabled={edition.openFlags > 0 || approve.isPending || !edition.version}
          title={edition.openFlags ? "Resolve every flagged claim first" : "Fix this version as the one that may be published"}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-ink text-white disabled:opacity-40"
        >
          {edition.openFlags ? `Approve · ${edition.openFlags} left` : "Approve"}
        </button>
      ) : null}
      {anyApproved && !live ? (
        <button onClick={ask(publish, `Publish this to ${seo.site?.baseUrl}?`)} disabled={publish.isPending} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-coral text-white disabled:opacity-55">
          Publish
        </button>
      ) : null}
      {anyApproved && live ? (
        <button onClick={ask(publish, "Publish the newer approved version over the live one?")} disabled={publish.isPending} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-page text-ink hover:bg-line">
          Publish again
        </button>
      ) : null}
      {others && edition.approvedAt ? (
        <button onClick={() => translate.mutate()} disabled={translate.isPending} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-page text-ink hover:bg-line">
          Translate · ${seo.prices.translateArticle}
        </button>
      ) : null}
      {edition.version ? (
        <button onClick={() => write.mutate()} disabled={write.isPending} title="Write it again from the sources, as a new version" className="px-2.5 py-1.5 rounded-lg text-xs font-medium text-muted hover:text-ink">
          Rewrite · ${seo.prices.writeArticle}
        </button>
      ) : null}
      {live ? (
        <button onClick={ask(withdraw, "Take every language of this down from the site?")} disabled={withdraw.isPending} className="px-2.5 py-1.5 rounded-lg text-xs font-medium text-muted hover:text-coral">
          Withdraw
        </button>
      ) : null}
    </div>
  );
}

const TONE = { muted: "border-line bg-panel text-ink-2", good: "border-good/30 bg-good/8 text-ink", coral: "border-coral/30 bg-coral/8 text-ink" };
const Note = ({ tone, children }: { tone: keyof typeof TONE; children: React.ReactNode }) => (
  <p className={`mb-3 rounded-[10px] border px-3 py-2 text-[12.5px] leading-relaxed ${TONE[tone]}`}>{children}</p>
);

/**
 * The claims standing in the way of Approve, pinned above the article and never behind a tab on a desk.
 *
 * Three ways out, and only one of them for a name: a competitor's name cannot be stood behind, it can only come
 * out. "I stand behind this" makes the person the source, and says so where it stands.
 */
function Flags({ edition, sources, onChanged }: { edition: EditionView; sources: Array<ArticleSourceView & { text: string }>; onChanged: () => void }) {
  const open = edition.flags.filter((f) => f.resolution === "open");
  const m = useMutation({
    mutationFn: (v: { flagId: string; how: "removed" | "cited" | "stands" }) => api.resolveFlag(edition.id, v.flagId, v.how),
    onSuccess: onChanged,
  });
  const WHY: Record<string, string> = {
    no_source: "No source for this",
    not_in_source: "Not in the source it cites",
    unknown_source: "Cited a source it was not given",
    never_name: "Names something you never mention",
  };
  return (
    <div className="mb-4 rounded-xl border border-coral/35 bg-coral/6 p-3.5">
      <p className="text-[12px] font-bold uppercase tracking-[.06em] text-coral">
        {open.length} {open.length === 1 ? "claim" : "claims"} to resolve before this can be approved
      </p>
      <ul className="mt-2 flex flex-col gap-2">
        {open.map((f) => (
          <li key={f.id} className="bg-panel border border-line rounded-lg px-3 py-2.5">
            <p className="text-[10.5px] font-bold uppercase tracking-[.05em] text-muted">{WHY[f.kind] ?? f.kind}{f.detail ? ` — ${f.detail}` : ""}</p>
            <p className="text-[13px] leading-relaxed mt-1">{plain(f.sentence)}</p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              <button onClick={() => m.mutate({ flagId: f.id, how: "removed" })} className="px-2.5 py-1 rounded-lg text-[11.5px] font-semibold bg-ink text-white">Remove it</button>
              {f.kind === "never_name" ? null : (
                <>
                  <button
                    onClick={() => m.mutate({ flagId: f.id, how: "cited" })}
                    disabled={!sources.length}
                    title={sources.length ? "Mark it as covered by a source you have" : "There are no sources to cite yet"}
                    className="px-2.5 py-1 rounded-lg text-[11.5px] font-medium bg-page text-ink-2 hover:bg-line disabled:opacity-40"
                  >
                    Cite a source
                  </button>
                  <button
                    onClick={() => { if (confirm("Record you as the source for this sentence?")) m.mutate({ flagId: f.id, how: "stands" }); }}
                    className="px-2.5 py-1 rounded-lg text-[11.5px] font-medium bg-page text-ink-2 hover:bg-line"
                  >
                    I stand behind this
                  </button>
                </>
              )}
            </div>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[11.5px] text-muted leading-relaxed">
        Code checks sources, names and links. It cannot tell an honest sentence from a hint at a competitor — that
        part is yours.
      </p>
    </div>
  );
}

/** The article as it will read, editable in place. Every edit is re-checked, so the flags follow the words. */
function Body({ edition, onChanged }: { edition: EditionView; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ title: edition.title, metaDescription: edition.metaDescription, bodyMd: edition.reviewMd });
  useEffect(() => { setDraft({ title: edition.title, metaDescription: edition.metaDescription, bodyMd: edition.reviewMd }); }, [edition.id, edition.version]);
  const m = useMutation({ mutationFn: () => api.editEdition(edition.id, draft), onSuccess: () => { setEditing(false); onChanged(); } });
  const cited = new Map(edition.claims.map((c) => [c.sentence, c.refs]));

  if (editing) {
    return (
      <div className="flex flex-col gap-2 max-w-3xl">
        <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} maxLength={200}
          className="w-full bg-panel border border-line rounded-lg px-3 py-2 text-[15px] font-semibold" aria-label="Title" />
        <input value={draft.metaDescription} onChange={(e) => setDraft({ ...draft, metaDescription: e.target.value })} maxLength={400}
          className="w-full bg-panel border border-line rounded-lg px-3 py-2 text-[12.5px]" aria-label="Meta description" />
        <textarea value={draft.bodyMd} onChange={(e) => setDraft({ ...draft, bodyMd: e.target.value })} rows={22}
          className="w-full bg-panel border border-line rounded-lg px-3 py-2 text-[13px] font-mono leading-relaxed" aria-label="The article" />
        <p className="text-[11.5px] text-muted">
          Keep the <code>[S2]</code> markers on the sentences they belong to: they are how a claim is traced, and they
          never appear on the published page.
        </p>
        <div className="flex gap-2">
          <button onClick={() => m.mutate()} disabled={m.isPending} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-ink text-white">Save and re-check</button>
          <button onClick={() => setEditing(false)} className="px-3 py-1.5 rounded-lg text-xs font-medium text-muted hover:text-ink">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <article className="max-w-3xl">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-[11px] text-faint tabular-nums">{edition.path} · version {edition.version}</p>
          <h1 className="font-display text-[22px] md:text-[25px] font-bold leading-[1.15] tracking-[-.02em] mt-1 text-balance">{edition.title || "Untitled"}</h1>
          <p className="text-[12.5px] text-muted mt-1.5 leading-relaxed max-w-[60ch]">{edition.metaDescription}</p>
        </div>
        <button onClick={() => setEditing(true)} className="shrink-0 px-2.5 py-1 rounded-lg text-[12px] font-semibold bg-page text-ink-2 hover:bg-line">Edit</button>
      </div>
      <div className="mt-4 flex flex-col gap-2.5">
        {blocks(edition.reviewMd).map((b, i) =>
          b.heading
            ? <h2 key={i} className="text-[15px] font-semibold mt-2">{plain(b.text)}</h2>
            : <p key={i} className="text-[13.5px] leading-[1.75]">{marked(b.text, cited)}</p>)}
      </div>
      {edition.faq.length ? (
        <section className="mt-6 border-t border-line pt-4">
          <h2 className="text-[15px] font-semibold">Questions</h2>
          <dl className="mt-2 flex flex-col gap-2.5">
            {edition.faq.map((q) => (
              <div key={q.question}>
                <dt className="text-[13.5px] font-semibold">{plain(q.question)}</dt>
                <dd className="text-[13.5px] leading-[1.75] text-ink-2">{plain(q.answer)}</dd>
              </div>
            ))}
          </dl>
        </section>
      ) : null}
    </article>
  );
}

/** Markdown, barely: headings and paragraphs. Nothing here renders HTML, and nothing here ever will. */
export function blocks(markdown: string): Array<{ heading: boolean; text: string }> {
  return markdown.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean).map((p) =>
    p.startsWith("## ") ? { heading: true, text: p.slice(3).trim() } : { heading: false, text: p });
}

/**
 * The sentence as somebody will read it on the published page: the `[S1]` markers taken out.
 *
 * They are the machinery of the checking, not part of the writing — the editor says they never appear on the
 * published page, and a review screen that shows them is the one place the person is asked to read around them.
 */
export function plain(text: string): string {
  return text.replace(/\[(S\d+|B:[a-z]+|\?)\]/g, "").replace(/\s+([.,;:!?])/g, "$1").replace(/\s{2,}/g, " ").trim();
}

/**
 * A web address, or nothing. The text on this screen was written by a model from pages on the open web, and a
 * `javascript:` or `data:` link in it must not become something a click runs.
 */
function webHref(url: string | null | undefined): string | undefined {
  return url && /^(https?:\/\/|\/(?!\/))/i.test(url.trim()) ? url : undefined;
}

/**
 * A link in the text, as a link. The offer link at the foot of every article is markdown, and markdown on the
 * screen is the writing showing its workings — the page it will become has an anchor there, so this does too.
 */
function links(text: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const re = /\[([^\]]+)\]\(([^)\s]+)\)/g;
  let last = 0;
  for (let m = re.exec(text); m; m = re.exec(text)) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const href = webHref(m[2]!);
    out.push(href ? <a key={m.index} href={href} className="text-coral underline underline-offset-2 decoration-coral/40">{m[1]}</a> : m[1]);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/**
 * A cited sentence is underlined and carries the source it came from, so the checking is visible in the reading
 * rather than only in a panel — and an uncited one is plain, which is the whole point of drawing it this way.
 */
function marked(text: string, cited: Map<string, string[]>) {
  return text.split(/(?<=[.!?])\s+/).map((s, i) => {
    const bare = plain(s);
    const refs = cited.get(bare);
    return (
      <span key={i} className={refs ? "underline decoration-good/60 decoration-2 underline-offset-4" : ""}>
        {links(bare)}
        {refs?.length ? <sup className="ml-0.5 text-[9.5px] font-bold text-good tabular-nums no-underline" title={`From ${refs.join(", ")}`}>{refs.join(",")}</sup> : null}
        {" "}
      </span>
    );
  });
}

/** What the article was written from, in full, so a claim can be checked against the words it came from. */
function Sources({ edition, sources }: { edition: EditionView; sources: Array<ArticleSourceView & { text: string }> }) {
  const used = new Set(edition.claims.flatMap((c) => c.refs));
  const shown = sources.filter((s) => used.has(s.ref));
  if (!shown.length) return null;
  return (
    <section className="mt-5 max-w-3xl">
      <h2 className={`${KICKER} text-muted`}>What it was written from</h2>
      <ul className="mt-1.5 flex flex-col gap-1.5">
        {shown.map((s) => (
          <li key={s.ref} className="bg-panel border border-line rounded-lg px-3 py-2">
            <details>
              <summary className="text-[12.5px] font-semibold cursor-pointer flex items-center gap-2">
                <span className="px-1.5 py-0.5 rounded bg-good/12 text-[10px] font-bold tabular-nums text-good">{s.ref}</span>
                {s.title}
              </summary>
              <p className="mt-1.5 text-[12.5px] leading-relaxed text-ink-2 whitespace-pre-wrap">{s.text.slice(0, 4_000)}</p>
              {s.url ? <a href={webHref(s.url)} target="_blank" rel="noreferrer" className="text-[11.5px] text-coral underline">{s.url}</a> : null}
            </details>
          </li>
        ))}
      </ul>
    </section>
  );
}
